import { Chart } from "@/ui/organisms/Chart/Chart";
import { GroupedCategoryAxis } from "@/ui/organisms/Chart/Axis";
import {
    filterNullValuesFromChartData,
    formatCurrency,
    formatTooltipData,
} from "./utils/filterChartData";

export function DebtToAssets({
    isCondensed,
    data,
}: {
    isCondensed?: boolean;
    data?: any;
}) {
    const transformedData = (data || []).map((item) => ({
        ...item,
        debtToTotalAssetsComputed:
            item.debtToTotalAssetsFromRemittanceReport != null
                ? item.debtToTotalAssetsFromRemittanceReport
                : item.grossDebtToAssets,
        debtToLiquidAssetsComputed:
            item.debtToLiquidAssetsFromRemittanceReport != null
                ? item.debtToLiquidAssetsFromRemittanceReport
                : item.grossDebtToLiquidAssets,
    }));

    const filteredData = filterNullValuesFromChartData(transformedData);
    if (filteredData.length === 0) {
        return null;
    }

    return (
        <Chart
            title="Gross Debt to Assets"
            description="Helps to evaluate whether a company's leverage is reasonable on a liquidation (assets) basis. Accomplishes this by showing debt to asset ratios including total assets, liquid assets and cash in a single chart with benchmarks. This chart is often used by portfolio managers overseeing a variety of asset-backed loan positions."
            isCondensed={isCondensed}
            options={{
                data: filteredData,
                tooltip: {
                    mode: "single",
                },
                series: [
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "debtToTotalAssetsComputed",
                        yName: "Debt to Total Assets",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const ratioValue =
                                    datum.debtToTotalAssetsComputed;

                                return {
                                    heading: `${month}`,
                                    title: "Debt to Total Assets",
                                    data: formatTooltipData([
                                        {
                                            label: "Debt to Total Assets",
                                            value: `${ratioValue.toFixed(1)}x`,
                                        },
                                        {
                                            label: "Debt Balance EOP",
                                            value: formatCurrency(
                                                datum.debtBalanceEop,
                                            ),
                                        },
                                        {
                                            label: "Total Debt",
                                            value: formatCurrency(
                                                datum.totalDebt,
                                            ),
                                        },
                                        {
                                            label: "Total Assets",
                                            value: formatCurrency(
                                                datum.totalAssets,
                                            ),
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                    {
                        type: "line",
                        xKey: "month",
                        yKey: "debtToLiquidAssetsComputed",
                        yName: "Debt to Liquid Assets",
                        marker: {
                            enabled: false,
                        },
                        tooltip: {
                            renderer: (params) => {
                                const datum = params.datum;
                                const month = `${datum.month[1]} ${datum.month[0]}`;
                                const ratioValue =
                                    datum.debtToLiquidAssetsComputed;

                                return {
                                    heading: `${month}`,
                                    title: "Debt to Liquid Assets",
                                    data: formatTooltipData([
                                        {
                                            label: "Debt to Liquid Assets",
                                            value: `${ratioValue.toFixed(1)}x`,
                                        },
                                        {
                                            label: "Debt Balance EOP",
                                            value: formatCurrency(
                                                datum.debtBalanceEop,
                                            ),
                                        },
                                        {
                                            label: "Total Debt",
                                            value: formatCurrency(
                                                datum.totalDebt,
                                            ),
                                        },
                                        {
                                            label: "Liquid Assets",
                                            value: formatCurrency(
                                                datum.liquidAssets,
                                            ),
                                        },
                                    ]),
                                };
                            },
                        },
                    },
                ],
                axes: [
                    GroupedCategoryAxis.doubleDepth,
                    {
                        type: "number",
                        position: "right",
                        label: {
                            formatter: (params) => {
                                return `${params.value.toFixed(1)}x`;
                            },
                        },
                        keys: [
                            "debtToTotalAssetsComputed",
                            "debtToLiquidAssetsComputed",
                        ],
                    },
                ],
            }}
        />
    );
}
